"use client";

import { cn } from "@/lib/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import { BeforeAfterSlider } from "@/components/marketing/before-after-slider";
import { SectionShell, SectionContainer } from "../primitives";

export type BeforeAfterSectionProps = {
  className?: string;
};

/**
 * BeforeAfterSection - Displays two separate interactive before/after comparison blocks.
 *
 * Features:
 * - Two separate dark sections in sequence (matching live aideco.us)
 * - First block: Sketch to AI Render comparison
 * - Second block: Empty Room to Staged comparison
 * - Draggable slider handles for revealing before/after
 * - Accessible with keyboard navigation and ARIA labels
 * - Touch-friendly for mobile devices
 *
 * @param className - Additional CSS classes
 */
export function BeforeAfterSection({ className }: BeforeAfterSectionProps) {
  const { t } = useLingui();

  return (
    <>
      {/* First Before/After Block: Sketch to AI Render */}
      <SectionShell
        id="property"
        ariaLabel={t`Before and after image comparison - Sketch to Render`}
        variant="default"
        noPadding
        className={cn("py-6 sm:py-14 lg:py-20", className)}
      >
        <SectionContainer>
          {/* Section Header */}
          <div className="mb-10 md:mb-14">
            <p className="mb-4 text-sm font-medium uppercase tracking-widest text-gold-accent"><Trans>— Before &amp; After —</Trans></p>
            <h2 className="font-bold text-3xl text-gray-900 leading-tight md:text-4xl lg:text-5xl">
              <Trans>See the transformation from sketch to render</Trans>
            </h2>
            <p className="mt-4 max-w-2xl text-gray-600 text-base md:text-lg">
              <Trans>Drag the slider to compare the original architectural drawing with the AI-generated
              photorealistic render.</Trans>
            </p>
          </div>

          {/* First Slider: Sketch to AI Render */}
          <BeforeAfterSlider
            afterAlt={t`AI-generated photorealistic render of modern house`}
            afterImage="/images/before-after/render-after.webp"
            afterLabel={t`AI Render`}
            beforeAlt={t`Architectural sketch of modern house`}
            beforeImage="/images/before-after/sketch-before.webp"
            beforeLabel={t`Sketch`}
            compact
          />
        </SectionContainer>
      </SectionShell>

      {/* Second Before/After Block: Empty Room to Staged */}
      <SectionShell
        id="property"
        ariaLabel={t`Before and after image comparison - Empty Room to Staged`}
        variant="white"
        noPadding
        className={cn("py-6 sm:py-14 lg:py-20", className)}
      >
        <SectionContainer>
          {/* Section Header */}
          <div className="mb-10 md:mb-14">
            <p className="mb-4 text-sm font-medium uppercase tracking-widest text-gold-accent"><Trans>— Before &amp; After —</Trans></p>
            <h2 className="font-bold text-3xl text-gray-900 leading-tight md:text-4xl lg:text-5xl">
              <Trans>Transform empty spaces into furnished interiors</Trans>
            </h2>
            <p className="mt-4 max-w-2xl text-gray-600 text-base md:text-lg">
              <Trans>Drag the slider to see how AI virtual staging adds furniture and style to empty rooms.</Trans>
            </p>
          </div>

          {/* Second Slider: Empty Room to Staged */}
          <BeforeAfterSlider
            afterAlt={t`Virtually staged living room with modern furniture`}
            afterImage="/images/before-after/staged-room-after.webp"
            afterLabel={t`Staged`}
            beforeAlt={t`Empty living room before virtual staging`}
            beforeImage="/images/before-after/empty-room-before.webp"
            beforeLabel={t`Empty Room`}
            compact
          />
        </SectionContainer>
      </SectionShell>
    </>
  );
}
